import React, { lazy, Suspense } from "react";
import { Box } from "@mui/material";
import Navbar from "../widgets/navbar/navbar";
import MenuDrawer from "../../../Components/Widgets/MenuDrawer";
import SubMenuDrawer from "../../../Components/Widgets/SubMenuDrawer";
import Footer from "../widgets/footer";
import RevealSection from "../widgets/common/reveal-section";
import { HeroPlaceholder } from "../widgets/common/home-section-skeleton";
import { useTheme } from "@mui/material/styles";

const DualImageHeroSlide = lazy(
  () => import("../../default/widgets/sliders/dual-image-hero-slide"),
);

const ImageBackroundEffect = lazy(
  () => import("../../default/widgets/sliders/image-backround-effect"),
);

const ProductsShowcaseSlider = lazy(
  () => import("../../../Components/Widgets/Sliders/ProductsShowcaseSlider"),
);

const ShopCategoryGrid = lazy(
  () => import("../../default/widgets/sliders/shop-category-grid"),
);

const ExploreMoreSection = lazy(
  () => import("../../default/widgets/sliders/explore-more-section"),
);

const RecentlyViewed = lazy(
  () => import("../../../Components/Widgets/RecentlyViewed"),
);

const ExportDefault = (props) => {
  const theme = useTheme();
  const gap = { xs: 6, md: 8 };
  const pairGap = { xs: 3, md: 4 };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Navbar bg_color="#fff" {...props} />
      <MenuDrawer {...props} />
      <SubMenuDrawer {...props} />
      <Suspense fallback={<HeroPlaceholder theme={theme} />}>
        <DualImageHeroSlide
          {...{
            ...props,
            mb: gap,
            slide: {
              imagesPath: "/marketing/main",
              images: {
                main: "men-maritime-legacy-main",
                secondary: "men-maritime-legacy-secondary",
              },
              title: "MARITIME LEGACY",
              desc: "Discover our curation of men’s designs rooted in Greek heritage and maritime legend, where anchors, tridents, ship wheels, and skull motifs capture the spirit of life at sea.",
              descMaxWidth: { xs: "unset", sm: "720px" },
              buttons: [
                {
                  name: "SHOP MARITIME DESIGNS",
                  url: "/men/curation/maritime-legacy",
                },
              ],
            },
          }}
        />
      </Suspense>

      <RevealSection id="categoriesAndMaterials" variant="slider">
        <ShopCategoryGrid
          {...{
            ...props,
            mb: gap,
            title: "SHOP BY",
            imagesPath: "/marketing/main",
            tabs: [
              {
                slug: "gategory",
                name: "CATEGORY",
                slides: [
                  {
                    slug: "mens-rings",
                    title: "Rings",
                    url: "/men/type/ring",
                  },
                  {
                    slug: "mens-pendants",
                    title: "Pendants & Talismans",
                    url: "/men/type/pendant",
                  },
                  {
                    slug: "mens-bracelets",
                    title: "Bracelets",
                    url: "/men/type/bracelet",
                  },
                  {
                    slug: "mens-earrings",
                    title: "Earrings",
                    url: "/men/type/earrings",
                  },
                  {
                    slug: "mens-necklaces",
                    title: "Necklaces & Chains",
                    url: "/men/type/necklace",
                  },
                  {
                    slug: "mens-shop-all",
                    title: "All",
                    url: "/men/types/all",
                    showOnDesktop: false,
                  },
                ],
              },
              {
                slug: "material",
                name: "MATERIAL",
                slides: [
                  {
                    slug: "men-gold",
                    title: "Gold",
                    url: "/men/metal/18kt-gold",
                  },
                  {
                    slug: "men-mixed-metals",
                    title: "Mixed Metals",
                    url: "/men/metal/mixed-metals",
                  },
                  {
                    slug: "men-sterling-silver",
                    title: "Sterling Silver",
                    url: "/men/metal/sterling-silver",
                  },
                  {
                    slug: "men-leather",
                    title: "Leather",
                    url: "/men/metal/leather",
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection
        id="imageBackgroundBestSellers"
        variant="banner"
        minHeight="100svh"
        mode="load-only"
      >
        <ImageBackroundEffect
          {...{
            ...props,
            mb: pairGap,
            slide: {
              slug: "men-best-sellers",
              url: "/",
              imagesPath: "/marketing/main",
              content: {
                title: "BEST SELLERS",
                desc: "Explore our top-selling designs, crafted with timeless detail and signature artistry. From everyday statements to meaningful gifts, each piece is made to stand out and be cherished.",
                descMaxWidth: { xs: "unset", sm: "500px" },
                buttons: [
                  {
                    name: "SHOP BEST SELLERS",
                    url: "/men/curation/best-sellers",
                  },
                ],
                position: {
                  mobile: {
                    bottom: "40px",
                    left: "24px",
                    right: "24px",
                  },
                  desktop: {
                    top: "40px",
                    right: { md: "40px", lg: "40px" },
                  },
                },
              },
            },
          }}
        />
      </RevealSection>

      <RevealSection id="bestSellersProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: gap,
            params: {
              pageAmount: 100,
              curation: "best-sellers",
              gender: "men",
              type: "ring,bracelet,earrings,pendant,necklace",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: true,
            },
          }}
        />
      </RevealSection>

      <RevealSection id="featuredSlider" variant="slider">
        <ExploreMoreSection
          {...{
            ...props,
            mb: gap,
            title: "EXPLORE MEN'S SIGNATURE DESIGNS",
            imagesPath: "/marketing/main",
            slides: [
              {
                slug: "men-sacred-symbols",
                url: "/men/curation/sacred-symbols",
                buttons: [
                  {
                    name: "Sacred Symbols",
                    url: "/men/curation/sacred-symbols",
                  },
                ],
              },
              {
                slug: "men-animal-emblems",
                url: "/men/curation/animal-emblems",
                buttons: [
                  {
                    name: "Animal Emblems",
                    url: "/men/curation/animal-emblems",
                  },
                ],
              },
              {
                slug: "men-blue-statements",
                url: "/men/curation/blue-statements",
                buttons: [
                  {
                    name: "Blue Statements",
                    url: "/men/curation/blue-statements",
                  },
                ],
              },
              {
                slug: "men-zodiacs",
                url: "/men/collection/zodiac",
                buttons: [
                  {
                    name: "Zodiacs SIGNS",
                    url: "/men/collection/zodiac",
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection
        id="imageBackgroundNewDesigns"
        variant="banner"
        minHeight="100svh"
        mode="load-only"
      >
        <ImageBackroundEffect
          {...{
            ...props,
            mb: pairGap,
            slide: {
              slug: "men-new-designs",
              url: "/",
              imagesPath: "/marketing/main",
              content: {
                title: "NEW FROM KONSTANTINO",
                desc: "Discover bold new creations from our latest men’s collections, crafted with powerful detail, signature artistry, and a refined masculine edge.",
                descMaxWidth: { xs: "unset", sm: "420px" },
                buttons: [
                  {
                    name: "SHOP NEW DESIGNS",
                    url: "/men/curation/new-arrivals",
                  },
                ],
                position: {
                  mobile: {
                    bottom: "40px",
                    left: "24px",
                    right: "24px",
                  },
                  desktop: {
                    top: "50%",
                    left: { md: "40px", lg: "80px" },
                  },
                },
              },
            },
          }}
        />
      </RevealSection>

      <RevealSection id="nedaProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: gap,
            params: {
              pageAmount: 100,
              curation: "new-arrivals",
              gender: "men",
              type: "ring,bracelet,earrings,pendant,necklace",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: true,
            },
          }}
        />
      </RevealSection>

      <RevealSection id="menCollections" variant="slider">
        <ExploreMoreSection
          {...{
            ...props,
            mb: gap,
            title: "SHOP MEN'S COLLECTIONS",
            imagesPath: "/marketing/main",
            buttons: [
              {
                name: "VIEW ALL",
                url: "/men/collectionS",
              },
            ],
            slides: [
              {
                slug: "kavafi-collection",
                url: "/men/collection/kavafi",
                buttons: [
                  {
                    name: "KAVAFI COLLECTION",
                    url: "/men/collection/kavafi",
                  },
                ],
                description:
                  "Explore stylish creations inspired by warrior armor from the Archaic and Hellenistic periods, designed in 18k gold with precious embedded stones.",
              },
              {
                slug: "kavos-collection",
                url: "/men/collection/kavos",
                buttons: [
                  {
                    name: "KAVOS COLLECTION",
                    url: "/men/collection/kavos",
                  },
                ],
                description:
                  "Discover nautical designs that echo Greek fishing ships and artful rope motifs.",
              },
              {
                slug: "alchemy-collection",
                url: "/men/collection/alchemy",
                buttons: [
                  {
                    name: "ALCHEMY COLLECTION",
                    url: "/men/collection/alchemy",
                  },
                ],
                description:
                  "Experience the captivating allure of intricately designed serpentine patterns that exude unique and irresistible charm.",
              },
              {
                slug: "hydra-collection",
                url: "/men/collection/hydra",
                buttons: [
                  {
                    name: "HYDRA COLLECTION",
                    url: "/men/collection/hydra",
                  },
                ],
                description:
                  "Discover designs that evoke hidden treasures, untold journeys, and timeless discovery.",
              },
              {
                slug: "laconia-collection",
                url: "/men/collection/laconia",
                buttons: [
                  {
                    name: "LACONIA COLLECTION",
                    url: "/men/collection/laconia",
                  },
                ],
                description:
                  "Explore a fresh wave of men’s designs inspired by ancient Spartan armor through emblematic engravings and motifs.",
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection id="recentlyViewed" variant="products">
        <RecentlyViewed
          {...props}
          mb={gap}
          title="ITEMS YOU VIEWED"
          productDisplay={{
            showName: false,
            showCollection: false,
            showPrice: false,
            showGems: false,
            showCuration: true,
          }}
        />
      </RevealSection>

      <Footer {...props} />
    </Box>
  );
};

export default ExportDefault;
